export type Division = 'D-I' | 'D-II' | 'D-III' | 'NAIA' | 'NJCAA' | 'OTHER' | 'ALL';

export interface Category {
  id: string;
  name: string;
  division: Division;
}

export interface RankedSchool {
  id: string;
  name: string;
  conferenceId: string;
  division: Exclude<Division, 'ALL'>;
  primary: string;
  secondary: string;
  categoryCents: number;
  contributorCount: number;
  rank: number;
}

export interface SchoolSearchResult {
  id: string;
  name: string;
  conferenceId: string;
  division: Exclude<Division, 'ALL'>;
  primary: string;
  secondary: string;
}

export interface School {
  id: string;
  name: string;
  conferenceId: string;
  division: Exclude<Division, 'ALL'>;
  totalCents: number;
}

export interface SchoolSuggestion {
  id: string;
  name: string;
  conferenceId?: string;
  note?: string;
  createdAt: string;
}

export interface Season {
  id: string;
  name: string;
  kind: 'all-time' | 'academic-year' | 'monthly';
  start?: string;
  end?: string;
}

const API_BASE = import.meta.env.VITE_API_BASE ?? '/api';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { 'content-type': 'application/json' },
    ...init,
  });
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const b = await res.json();
      if (b?.message) message = b.message;
    } catch {
      /* non-JSON error body */
    }
    throw new Error(message);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const api = {
  listCategories(): Promise<{ categories: Category[] }> {
    return request('/categories');
  },
  leaderboard(categoryId: string): Promise<{ category: Category; schools: RankedSchool[] }> {
    return request(`/categories/${encodeURIComponent(categoryId)}/leaderboard`);
  },
  listSeasons(): Promise<{ seasons: Season[] }> {
    return request('/seasons');
  },
  seasonLeaderboard(
    seasonId: string,
    categoryId: string,
  ): Promise<{ season: Season; category: Category; schools: RankedSchool[] }> {
    // The all-time board keeps its original route (legacy rows); every other
    // season is its own top-level board under /seasons/<id>/.
    if (seasonId === 'all') {
      return request(`/categories/${encodeURIComponent(categoryId)}/leaderboard`);
    }
    return request(
      `/seasons/${encodeURIComponent(seasonId)}/categories/${encodeURIComponent(categoryId)}/leaderboard`,
    );
  },
  searchSchools(q: string): Promise<{ results: SchoolSearchResult[] }> {
    return request(`/schools/search?q=${encodeURIComponent(q)}`);
  },
  contribute(schoolId: string, amountCents: number, handle?: string): Promise<{ url: string }> {
    return request(`/schools/${encodeURIComponent(schoolId)}/contribute`, {
      method: 'POST',
      body: JSON.stringify({ amountCents, handle }),
    });
  },
  suggestSchool(input: {
    name: string;
    conferenceId?: string;
    note?: string;
  }): Promise<{ suggestion: SchoolSuggestion }> {
    return request('/schools/suggest', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
};

/** Format cents as USD, e.g. 12345 -> "$123.45". */
export function formatCents(cents: number): string {
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}
